<template>
    <div class="video-player">
        <video
            playsinline
            :src="streamUrl"
            ref="video"
        />
        <div class="video-player__controls">
            <Icon
                @click="togglePlayback"
                :class="isVideoPlaying ? 'icon-media_pause': 'icon-media_play'"
            />
            <Icon
                @click="$emit('toggleSound')"
                :class="isSoundOn ? 'icon-audio_stream' : 'icon-audio_stream_off'"
            />
        </div>
    </div>
</template>

<script>
    import { loadScript } from '@superbet-group/web.lib.utilities';
    import Vue from 'vue';
    import { mapActions } from 'vuex';

    import { logger } from '@app/logger';
    import { sendMultimediaRequestResultAnalytic } from '@core/services/media/MediaService';

    let hlsVideoNeedsProccessing;

    export default Vue.component('VideoPlayer', {
        name: 'VideoPlayer',
        emits: [
            'error',
            'toggleSound',
        ],
        props: {
            streamUrl: {
                type: String,
                required: true,
            },
            videoType: {
                type: String,
                required: true,
            },
            rerenderingIndicator: {
                type: [String, Number],
                default: '',
            },
            isSoundOn: {
                type: Boolean,
                required: true,
            },
        },
        data() {
            return {
                isVideoPlaying: false,
                shouldProcessVideo: false,
            };
        },
        async mounted() {
            if (hlsVideoNeedsProccessing === undefined) {
                hlsVideoNeedsProccessing = !this.checkCompatibility();
            }

            if (hlsVideoNeedsProccessing) {
                await this.loadRequirements();
                this.prepareVideo();
            }
            this.initializeVideoPlayer();
        },
        async updated() {
            if (this.shouldProcessVideo) {
                await this.prepareVideo();
                this.initializeVideoPlayer();
            }
        },
        watch: {
            rerenderingIndicator(newValue, oldValue) {
                if (newValue !== oldValue) {
                    const video = this.$refs.video;
                    const isPlaying = video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
                    if (!isPlaying && this.isVideoPlaying) {
                        this.playVideo();
                    }
                }
            },
            streamUrl() {
                if (hlsVideoNeedsProccessing) {
                    if (this.videoWrapper) {
                        this.videoWrapper.destroy();
                    }
                    this.shouldProcessVideo = true;
                } else {
                    this.initializeVideoPlayer();
                }
            },
            isSoundOn() {
                this.setVolume();
            },
        },
        methods: {
            ...mapActions('errors', [
                'showError',
            ]),
            reportError(error, additionalMessages = []) {
                sendMultimediaRequestResultAnalytic(
                    this.expandedEventData,
                    'false',
                    error,
                    additionalMessages,
                );
            },
            checkCompatibility() {
                const video = this.$refs.video;
                return video.canPlayType(this.videoType);
            },
            async loadRequirements() {
                const hlsUrl = `${window.location.origin}/static/js/hls-0_12_4.js`;
                const preparatorUrl = `${window.location.origin}/static/js/prepareVideo-1_0_1.js`;

                await loadScript(hlsUrl, 'hls');
                await loadScript(preparatorUrl, 'videoPreparator');
            },
            async playVideo() {
                const video = this.$refs.video;
                if (!video) {
                    return;
                }
                try {
                    await video.play();
                    this.isVideoPlaying = true;
                    if (this.videoWrapper) {
                        this.videoWrapper.startLoad();
                    }
                } catch (e) {
                    logger.error(e);
                    this.reportError(new Error('Failed to play video'), [e?.message]);
                }
            },
            pauseVideo() {
                if (this.isVideoPlaying === false) {
                    return;
                }
                const video = this.$refs.video;
                if (!video) {
                    return;
                }
                video.pause();
                this.isVideoPlaying = false;
                if (this.videoWrapper) {
                    this.videoWrapper.stopLoad();
                }
            },
            togglePlayback() {
                if (this.isVideoPlaying) {
                    this.pauseVideo();
                } else {
                    this.playVideo();
                }
            },
            setVolume() {
                this.$refs.video.volume = this.isSoundOn ? 1 : 0;
            },
            initializeVideoPlayer() {
                this.playVideo();
                this.setVolume();
            },
            async prepareVideo() {
                try {
                    this.videoWrapper = await window.prepareVideo(this.$refs.video, this.streamUrl);
                } catch (e) {
                    this.$emit('error');
                    this.reportError(new Error('Failed to prepare video'), [e?.message]);
                }
            },
        },
        beforeDestroy() {
            if (this.videoWrapper) {
                this.videoWrapper.stopLoad();
                this.videoWrapper.destroy();
            }
        },
    });
</script>
